import React from 'react';
import { Autocomplete } from '@material-ui/lab';
import { AxiosError, AxiosResponse } from 'axios';
import { Button, createStyles, Grid, Icon, Link, makeStyles, TextField, Theme, Typography } from '@material-ui/core';

import { ClientModel } from '../../../Models/ClientModel';
import { StilingueSpreadsheetModel } from '../../../Models/StilingueSpreadsheetModel';
import { StilingueSpreadsheetUploadModel } from '../../../Models/StilingueSpreadsheetUploadModel';
import ClientService from '../../../Services/ClientService';
import StilingueSpreadsheetService from '../../../Services/StilingueSpreadsheetService';
import { BasicDropzone } from '../../../shared/components/BasicDropzone';
import { PageLocation } from '../../../shared/enum/PageLocation';
import { AuthContext } from '../../Authentication/AuthContext';
import { HomeContext } from '../../Home/HomeContext';
import { useInputStyles } from '../../../shared/components/UploadStyles';
import { StilingueSpreadsheetConfigurationModal } from '../components/StilingueSpreadsheetConfigurationTable';

interface IStilingueSpreadsheetUploadInputProps {
    onUpload: (data: StilingueSpreadsheetUploadModel, file: File) => void,
}

export const useStyles = makeStyles((theme: Theme) => createStyles({
    textLink: {
        marginTop: '-12px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'start',
        textDecoration: 'none',
        color: theme.palette.text.primary
    },
}));

export function StilingueSpreadsheetUploadInput({ onUpload }: IStilingueSpreadsheetUploadInputProps) {
    const classes = useInputStyles();

    const authContext = React.useContext(AuthContext);
    const homeContext = React.useContext(HomeContext);

    const [file, setFile] = React.useState<File | null>(null);
    const [clientList, setClientList] = React.useState<ClientModel[]>([]);
    const [selectedClient, setSelectedClient] = React.useState<ClientModel | null>(null);
    const [spreadsheetModelList, setSpreadsheetModelList] = React.useState<StilingueSpreadsheetModel[]>([]);
    const [selectedSpreadsheetModel, setSelectedSpreadsheetModel] = React.useState<StilingueSpreadsheetModel | null>(null);

    const [clientLoading, setClientLoading] = React.useState<boolean>(false);
    const [spreadsheetModelLoading, setSpreadsheetModelLoading] = React.useState<boolean>(false);

    const [isConfigModalOpen, setIsConfigModalOpen] = React.useState<boolean>(false);

    React.useEffect(() => {

        if (homeContext.pageLocation === PageLocation.SOCIAL_MEDIA_UPLOAD) {

            homeContext.setOpenLoadingDialog(true);
            fetchClients();
            fetchSpreadsheetModels();
        }
    }, [homeContext.pageLocation]);

    React.useEffect(() => {
        if (!clientLoading && !spreadsheetModelLoading)
            homeContext.setOpenLoadingDialog(false);
    }, [clientLoading, spreadsheetModelLoading]);

    const fetchClients = () => {
        setClientLoading(true);

        ClientService.clientByUserService(authContext.login.user.id)
            .getAll()
            .then((res: AxiosResponse) => {
                setClientList(res.data);

                if (res.data.length === 1)
                    setSelectedClient(res.data[0]);
            })
            .catch((error: AxiosError) => homeContext.showError('', error))
            .finally(() => setClientLoading(false));
    };

    const fetchSpreadsheetModels = () => {
        setSpreadsheetModelLoading(true);

        StilingueSpreadsheetService
            .stilingueSpreadsheetService()
            .getAll()
            .then((res: AxiosResponse) => {
                setSpreadsheetModelList(res.data);

                if (res.data.length === 1)
                    setSelectedSpreadsheetModel(res.data[0]);
            })
            .catch((error: AxiosError) => homeContext.showError('', error))
            .finally(() => setSpreadsheetModelLoading(false));

    };

    const handleSubmitSpreadsheet = () => {
        if (selectedClient && selectedSpreadsheetModel && file) {

            const uploadData: StilingueSpreadsheetUploadModel = {
                clientId: selectedClient.id,
                stilingueSpreadsheetId: selectedSpreadsheetModel.id,
                uploadDate: new Date(Date.now())
            };

            onUpload(uploadData, file);
        }
    };

    const getTitleAndSubtitle = () => {
        return (
            <>
                <Grid item>
                    <Typography variant="h3" className={classes.title}>
                        Insira os dados solicitados
                    </Typography>
                </Grid>
                <Grid item>
                    <Typography variant="body1" className={classes.item}>
                        {'Insira uma planilha que esteja no formato .xls ou .xlsx. ' +
                            'Indique nos campos solicitados qual é o cliente e modelo de planilha. '}
                    </Typography>
                </Grid>
            </>
        );
    };

    const getClientField = () => {
        return (
            <Grid item xs={12}>
                <Autocomplete
                    id="clients-field"
                    value={selectedClient}
                    onChange={(_, newValue: ClientModel | null) => { setSelectedClient(newValue); }}
                    options={clientList}
                    getOptionLabel={(option: ClientModel) => option.name}
                    className={classes.fields}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            label="Cliente"
                            variant="outlined" />
                    } />
            </Grid>
        );
    };

    const getTemplateField = () => {
        return (
            <Grid item xs={12}>
                <Autocomplete
                    id="template-field"
                    value={selectedSpreadsheetModel}
                    onChange={(_, newValue: StilingueSpreadsheetModel | null) => { setSelectedSpreadsheetModel(newValue); }}
                    options={spreadsheetModelList}
                    getOptionLabel={(option: StilingueSpreadsheetModel) => option.description}
                    className={classes.fields}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            label="Modelo"
                            variant="outlined" />
                    } />
            </Grid>
        );
    };

    const getSpreadsheetColumnConfiguration = () => {
        return (
            <Grid item>
                <Link
                    href="#"
                    className={classes.textLink}
                    onClick={() => { if (selectedSpreadsheetModel) setIsConfigModalOpen(true); }}>
                    <Icon>help_outline</Icon>
                    <Typography variant="caption" className={classes.itemDetail}>
                        Ver configurações de campos da planilha
                    </Typography>
                </Link>
            </Grid>
        );
    };

    const getSubmitButton = () => {
        return (
            <Grid item>
                <Button
                    size="large"
                    variant="contained"
                    color="primary"
                    className={classes.fields}
                    disabled={(!selectedClient || !selectedSpreadsheetModel || !file)}
                    onClick={() => handleSubmitSpreadsheet()}>
                    Próximo
                </Button>
            </Grid>
        );
    };

    return (
        <>
            <Grid
                container
                direction="column"
                justify="center"
                alignItems="center">

                {getTitleAndSubtitle()}
                {getClientField()}
                {getTemplateField()}
                {selectedSpreadsheetModel && getSpreadsheetColumnConfiguration()}
                <BasicDropzone onChangefile={(file: File) => setFile(file)} isClipperSpreadsheetUpload={false} />
                {getSubmitButton()}
            </Grid>
            { selectedSpreadsheetModel && (
                <StilingueSpreadsheetConfigurationModal
                    isModalOpen={ isConfigModalOpen }
                    setIsModalOpen={ setIsConfigModalOpen }
                    stilingueSpreadsheet={ selectedSpreadsheetModel } />
            ) }
        </>
    );
}